import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import BrandLogo from '@/components/shell/BrandLogo'
import LanguageSwitcher from '@/components/shell/LanguageSwitcher'
import ThemeSwitcher from '@/components/shell/ThemeSwitcher'
import { useAuth } from '@/providers/AuthProvider'

function deriveDefaultUsername(email?: string | null, displayName?: string | null) {
  const safeDisplayName = (displayName || '').trim()
  if (safeDisplayName && !safeDisplayName.includes('@')) {
    return safeDisplayName
  }

  const localPart = (email || '').split('@')[0] || ''
  return localPart.split('+')[0]?.trim() || ''
}

export default function EnrollmentScreen() {
  const { t, i18n } = useTranslation()
  const {
    authError,
    completeEnrollment,
    logout,
    user,
    userProfile,
  } = useAuth()
  const [username, setUsername] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [language, setLanguage] = useState('fr')
  const [organizationName, setOrganizationName] = useState('')
  const [isPersonalOrganization, setIsPersonalOrganization] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const email = userProfile?.email || user?.email || ''
  const defaultUsername = useMemo(
    () =>
      user?.username ||
      userProfile?.username ||
      deriveDefaultUsername(user?.email, user?.displayName) ||
      '',
    [user?.displayName, user?.email, user?.username, userProfile?.username],
  )
  const shouldCollectOrganization = false
  const subtitle = shouldCollectOrganization
    ? t('enrollment.subtitle')
    : t('enrollment.subtitleProfileOnly')
  const roleHint = shouldCollectOrganization
    ? t('enrollment.roleHint')
    : t('enrollment.roleHintProfileOnly')

  useEffect(() => {
    if (!username) {
      setUsername(defaultUsername)
    }
  }, [defaultUsername, username])

  useEffect(() => {
    const resolvedFirstName = userProfile?.firstName || user?.firstName
    if (resolvedFirstName && !firstName) {
      setFirstName(resolvedFirstName)
    }
    const resolvedLastName = userProfile?.lastName || user?.lastName
    if (resolvedLastName && !lastName) {
      setLastName(resolvedLastName)
    }
    const resolvedLanguage = userProfile?.language || user?.language
    if (resolvedLanguage) {
      setLanguage(resolvedLanguage)
      return
    }
    const currentLanguage = i18n.resolvedLanguage || i18n.language || 'fr'
    setLanguage(currentLanguage.startsWith('es') ? 'es' : currentLanguage.startsWith('en') ? 'en' : 'fr')
  }, [
    firstName,
    i18n.language,
    i18n.resolvedLanguage,
    lastName,
    user?.firstName,
    user?.language,
    user?.lastName,
    userProfile?.firstName,
    userProfile?.language,
    userProfile?.lastName,
  ])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setLocalError(null)

    if (!username.trim()) {
      setLocalError(t('enrollment.validation.username'))
      return
    }
    if (!firstName.trim()) {
      setLocalError(t('enrollment.validation.firstName'))
      return
    }
    if (!lastName.trim()) {
      setLocalError(t('enrollment.validation.lastName'))
      return
    }
    if (!language.trim()) {
      setLocalError(t('enrollment.validation.language'))
      return
    }
    if (shouldCollectOrganization && !isPersonalOrganization && !organizationName.trim()) {
      setLocalError(t('enrollment.validation.organizationName'))
      return
    }

    setIsSubmitting(true)
    try {
      await completeEnrollment({
        username: username.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        language: language.trim(),
        organization_name: shouldCollectOrganization ? organizationName.trim() || undefined : undefined,
        is_personal_organization: shouldCollectOrganization ? isPersonalOrganization : false,
      })
      await i18n.changeLanguage(language)
    } catch (error) {
      setLocalError(error instanceof Error ? error.message : t('enrollment.error'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-10">
      <div className="w-full max-w-2xl rounded-3xl border border-border bg-card p-8 shadow-sm">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <BrandLogo className="mb-3 max-w-[13rem]" />
            <h1 className="text-3xl font-semibold tracking-tight">{t('enrollment.title')}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <div className="flex items-center gap-3">
            <ThemeSwitcher compact />
            <LanguageSwitcher compact />
          </div>
        </div>

        <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {roleHint}
        </div>

        {(localError || authError) && (
          <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {localError || authError}
          </div>
        )}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">{t('enrollment.email')}</span>
              <input
                type="email"
                value={email}
                disabled
                className="w-full rounded-xl border border-border bg-muted px-3 py-2 text-foreground/70"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">{t('enrollment.username')}</span>
              <input
                type="text"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder={t('enrollment.usernamePlaceholder')}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">{t('enrollment.firstName')}</span>
              <input
                type="text"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder={t('enrollment.firstNamePlaceholder')}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">{t('enrollment.lastName')}</span>
              <input
                type="text"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                placeholder={t('enrollment.lastNamePlaceholder')}
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">{t('enrollment.language')}</span>
            <select
              value={language}
              onChange={(event) => setLanguage(event.target.value)}
              className="w-full rounded-xl border border-border bg-background px-3 py-2"
            >
              <option value="fr">{t('language.fr')}</option>
              <option value="en">{t('language.en')}</option>
              <option value="es">{t('language.es')}</option>
            </select>
          </label>

          {shouldCollectOrganization && (
            <div className="rounded-2xl border border-border p-4">
              <div className="flex items-start gap-3">
                <input
                  id="personal-organization"
                  type="checkbox"
                  checked={isPersonalOrganization}
                  onChange={(event) => setIsPersonalOrganization(event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-border"
                />
                <div className="space-y-1">
                  <label htmlFor="personal-organization" className="text-sm font-medium">
                    {t('enrollment.personalOrganization')}
                  </label>
                  <p className="text-sm text-muted-foreground">{t('enrollment.personalOrganizationHint')}</p>
                </div>
              </div>

              <label className="mt-4 block text-sm">
                <span className="mb-1 block text-muted-foreground">{t('enrollment.organizationName')}</span>
                <input
                  type="text"
                  value={organizationName}
                  onChange={(event) => setOrganizationName(event.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-3 py-2 outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder={
                    isPersonalOrganization
                      ? t('enrollment.organizationNamePersonalPlaceholder')
                      : t('enrollment.organizationNamePlaceholder')
                  }
                />
              </label>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" className="flex-1" disabled={isSubmitting}>
              {isSubmitting ? t('common.loading') : t('enrollment.submit')}
            </Button>
            <Button type="button" variant="outline" className="flex-1" onClick={() => void logout()}>
              {t('sidebar.switchUser')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
